import React, { useState } from "react";
import { SERVICE_OPTIONS } from "../data";
import { SectionLabel } from "../components/ui/Typography";
import { BtnOrange } from "../components/ui/Buttons";

const InputField = ({ label, type = "text", placeholder, value, onChange, required }) => (
  <div>
    <label className="block font-head font-semibold text-slate-600 text-sm mb-1.5">
      {label}{required && " *"}
    </label>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 text-slate-800 text-sm outline-none transition-all duration-200 focus:border-blue-600 focus:shadow-[0_0_0_3px_rgba(30,86,217,.10)]"
    />
  </div>
);

const ContactInfo = () => (
  <div className="space-y-5">
    <div className="bg-white border border-slate-200 rounded-2xl p-7">
      <h3 className="font-head font-bold text-slate-900 text-lg mb-5">Get in Touch</h3>
      {[
        {
          icon: <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>,
          label: "Email",
          val: "contact@eminentsolutions.net",
        },
        {
          icon: <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>,
          label: "Phone",
          val: "+92-XXX-XXXXXXX",
        },
        {
          icon: <><path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></>,
          label: "Address",
          val: "Plot #19-A, Sector 16, Main Korangi Road, Karachi, Pakistan",
        },
      ].map(item => (
        <div key={item.label} className="flex gap-4 mb-4 last:mb-0">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
            <svg viewBox="0 0 24 24" fill="none" stroke="#1e56d9" strokeWidth="2" className="w-5 h-5">
              {item.icon}
            </svg>
          </div>
          <div>
            <div className="text-xs font-head font-semibold text-slate-400 uppercase tracking-wide">{item.label}</div>
            <div className="text-slate-700 text-sm mt-0.5">{item.val}</div>
          </div>
        </div>
      ))}
    </div>

    {/* Social links */}
    <div className="bg-white border border-slate-200 rounded-2xl p-7">
      <h3 className="font-head font-bold text-slate-900 text-base mb-4">Follow Us</h3>
      <div className="flex flex-wrap gap-3">
        {[
          { name: "LinkedIn", color: "hover:border-blue-600 hover:text-blue-600" },
          { name: "Twitter",  color: "hover:border-sky-500 hover:text-sky-500" },
          { name: "YouTube",  color: "hover:border-red-500 hover:text-red-500" },
          { name: "Facebook", color: "hover:border-blue-700 hover:text-blue-700" },
        ].map(s => (
          <a
            key={s.name}
            href="#"
            className={`font-head font-semibold text-sm text-slate-600 border-2 border-slate-200 px-4 py-2 rounded-xl transition-all duration-150 ${s.color}`}
          >
            {s.name}
          </a>
        ))}
      </div>
    </div>
  </div>
);

const ContactForm = () => {
  const [form, setForm] = useState({ first: "", last: "", email: "", company: "", service: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | done

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus("loading");
    setTimeout(() => setStatus("done"), 1500);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
      <h3 className="font-head font-bold text-slate-900 text-xl mb-6">Send Us a Message</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <InputField label="First Name" placeholder="John"  value={form.first} onChange={set("first")} required />
          <InputField label="Last Name"  placeholder="Smith" value={form.last}  onChange={set("last")}  required />
        </div>
        <InputField label="Email Address" type="email" placeholder="john@company.com" value={form.email} onChange={set("email")} required />
        <InputField label="Company" placeholder="Your company name" value={form.company} onChange={set("company")} />

        {/* Select */}
        <div>
          <label className="block font-head font-semibold text-slate-600 text-sm mb-1.5">I'm Interested In</label>
          <select
            value={form.service}
            onChange={set("service")}
            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 text-slate-800 text-sm outline-none focus:border-blue-600 bg-white transition-all"
          >
            {SERVICE_OPTIONS.map(o => <option key={o}>{o}</option>)}
          </select>
        </div>

        {/* Textarea */}
        <div>
          <label className="block font-head font-semibold text-slate-600 text-sm mb-1.5">Message</label>
          <textarea
            rows={4}
            placeholder="Tell us about your project, challenges, or questions..."
            value={form.message}
            onChange={set("message")}
            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 text-slate-800 text-sm outline-none focus:border-blue-600 transition-all resize-y"
          />
        </div>

        {status === "done" ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-emerald-700 font-head font-semibold text-center">
            ✅ Message sent! We'll get back to you within 24 hours.
          </div>
        ) : (
          <BtnOrange full>
            {status === "loading" ? (
              <>
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spinSlow" />
                Sending…
              </>
            ) : (
              <>
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/>
                </svg>
                Send Message
              </>
            )}
          </BtnOrange>
        )}
      </form>
    </div>
  );
};

const ContactPage = () => (
  <div className="page-enter">
    {/* Hero */}
    <section className="bg-gradient-to-br from-slate-950 via-[#0d2151] to-[#0e2660] relative overflow-hidden pt-32 pb-20 text-center">
      <div className="hero-grid-bg absolute inset-0 opacity-50 pointer-events-none" />
      <div className="relative max-w-2xl mx-auto px-4 sm:px-6">
        <SectionLabel light>Contact Us</SectionLabel>
        <h1 className="font-head font-black text-5xl md:text-6xl text-white mt-3 mb-4 tracking-tight leading-tight">
          Let's Talk About<br /><span className="gradient-text">Your Next AI Project</span>
        </h1>
        <p className="text-white/60 text-xl">Reach out — our team gets back within 24 hours.</p>
      </div>
    </section>

    <section className="py-20 bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-2">
            <ContactInfo />
          </div>
          <div className="lg:col-span-3">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default ContactPage;
